/* global Office Blob navigator ClipboardItem console window URLSearchParams */
import * as React from "react";
import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dropdown,
  Label,
  TabList,
  Tab,
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  Option,
  ToggleButton,
  Input,
  makeStyles,
  MenuItem,
  MenuList,
  Spinner,
  Divider,
  SpinButton,
  Switch,
} from "@fluentui/react-components";
import CommonTooltip from "../common/CommonTooltip";
import {
  Add16Filled,
  ArrowExit20Filled,
  Save20Filled,
  Subtract16Filled,
  TextBold20Filled,
  TextItalic20Filled,
  TextUnderline20Filled,
} from "@fluentui/react-icons";
import {
  updateSheet,
  resetFromDefault,
  rename,
  importSheet,
  deleteSheet,
  abandonChangesOnAll,
  applyChangesOnAll,
  setSelectedSheet,
  storeSheets,
  loadSheets,
} from "../../state/styleSheetSlice";
import CustomColorPickerSubMenu from "../contextmenus/submenus/CustomColorPickerSubMenu";
import {
  openColorPicker,
  closeColorPicker,
  closeStyleSheetDialog,
  closeStyleSheetWizard,
  openStyleSheetWizard,
} from "../../state/appStateSlice";
import {
  Delete,
  Import,
  LineTypeSolid,
  StylesheetBorders,
  StylesheetColors,
  StylesheetDuplicate,
  StylesheetExport,
  StylesheetFonts,
  StylesheetRename,
  StylesheetReset,
  StylesheetSpacers,
  StylesheetSpacing,
  ShapesMenu,
  AlignTopLeft,
  AlignTopCenter,
  AlignTopRight,
  AlignCenterLeft,
  AlignCenterCenter,
  AlignCenterRight,
  AlignBottomLeft,
  AlignBottomCenter,
  AlignBottomRight,
} from "../../assets/icons";
import {
  availableShapes,
  getIconForShapeType,
  ShapePresetIcon,
  lineTypes,
} from "../contextmenus/menuitems/ShapeMenuItem";
import CellShape from "../cells/CellShape";
import { notify } from "../providers/GlobalToast";
import { Checkmark20Filled } from "@fluentui/react-icons";
import { XIcon } from "@phosphor-icons/react";
import { Hamburger } from "../../assets/icons";
import { storeLastSetting } from "../../state/appStateSlice";
import { getAllColors, /*getAllColorsCategorised,*/ getAllFonts, setAllFonts } from "../../ppt/templating";
import { Wand } from "lucide-react";
import StyleSheetWizard from "./StyleSheetWizard";
import { CaretDownIcon, GearIcon, InfoIcon, PaintBrushIcon } from "@phosphor-icons/react";
import exists from "../../helpers/exists";
import LineSpacingTable from "../styleSheetEditor/LineSpacingTable";
import { isEmbeddableCrossOriginIframe } from "../../helpers/embedBridge";
import { requestStyleSheetFromParent } from "../../helpers/loading";
import { debounce } from "../../helpers/debounce";

const useClasses = makeStyles({
  TabHeaderCheckMark: {
    width: 16,
    height: 16,
    verticalAlign: "middle",
    marginRight: 4,
  },
  SelectedSheetTitle: {
    fontSize: "16px",
  },
  Apply: {
    background: "#0065FD",
    ":hover": {
      background: "#0085FF",
    },
  },
  // Compact panel variant (?variant=panel): tighter type, sticky footer so the
  // Save/Cancel actions stay visible without the dialog chrome.
  PanelRoot: {
    fontSize: "12px",
  },
  PanelFooter: {
    position: "sticky",
    bottom: 0,
    background: "#FFFFFF",
    borderTop: "1px solid #E1E1E1",
    zIndex: 1,
  },
});

const StyleSheetEditor = (props) => {
  const { t } = useTranslation();
  const classes = useClasses();
  const dispatch = useDispatch();
  const store = useStore();
  // Host-panel mode: embeddable.html running inside SlideCraft's cross-origin
  // iframe. Persistence and the load/draft handshakes go through the parent
  // frame instead of Office/localStorage. `variant=panel` selects compact chrome.
  const isHostPanel = isEmbeddableCrossOriginIframe();
  const isPanelVariant =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("variant") === "panel";
  const colorPickerOpen = useSelector((state) => state.app.colorPickerOpen);
  const styleSheet = useSelector((state) => state.styleSheet);
  const [selectedSheetKey, setSelectedSheetKey] = useState(styleSheet?.selectedSheet ?? "default");
  const selectedSheet = useSelector(
    (state) => state.styleSheet.draftSheets[selectedSheetKey] ?? state.styleSheet.sheets[selectedSheetKey]
  );
  const lastShapeSettings = useSelector((state) => state.app.lastSettings.shape);
  const lastIconSettings = useSelector((state) => state.app.lastSettings.icon);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(null);
  const [openMenus, setOpenMenus] = useState({});
  const [openHamburger, setOpenHamburger] = useState(false);
  const openWizard = useSelector((state) => state.app.styleSheetWizardOpen);

  const presetsRef = useRef(null);
  const colorsRef = useRef(null);
  const bordersRef = useRef(null);
  const spacersRef = useRef(null);
  const spacingRef = useRef(null);
  const shapePresetsRef = useRef(null);
  const scrollOptions = { behaviour: "smooth", block: "start", inline: "start" };
  const [lastScrolledTab, setLastScrolledTab] = useState(null);
  const [selectedShapePresetName, setSelectedShapePresetName] = useState(null);

  useEffect(() => {
    setSelectedShapePresetName((prev) => {
      const presets = selectedSheet?.shapePresets;
      if (!presets?.length) return null;
      if (prev && presets.some((p) => p.name === prev)) return prev;
      return presets[0].name;
    });
  }, [selectedSheetKey]);

  const closeColorPickerMenu = (menuId) => {
    setOpenMenus((prev) => ({ ...prev, [menuId]: false }));
    dispatch(closeColorPicker());
  };

  function getBodyPreset(sheet) {
    return sheet?.presets?.find((p) => p.name === "Body");
  }

  function getPreset(name) {
    return selectedSheet?.presets?.find((p) => p.name === name);
  }

  // In panel mode each preset renders as two rows — the name in its own preview
  // style, then its settings just below. Otherwise the cells stay flat so the
  // parent six-column grid lays them out as one row per preset.
  const renderPresetBlock = (blockKey, previewCell, settingsCells) =>
    isPanelVariant ? (
      <div
        key={blockKey}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: "6px 0",
          borderBottom: "1px solid #ECECEC",
        }}
      >
        {previewCell}
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {settingsCells}
        </div>
      </div>
    ) : (
      <React.Fragment key={blockKey}>
        {previewCell}
        {settingsCells}
      </React.Fragment>
    );

  const [detectedColors, setDetectedColors] = useState([]);
  useEffect(() => {
    const gatherAllColors = async () => {
      const allColors = await getAllColors();
      console.warn("All colors detected", allColors);
      setDetectedColors(allColors);
    };
    gatherAllColors();
  }, []);
  const [detectedFonts, setDetectedFonts] = useState([]);
  useEffect(() => {
    const gatherAllFonts = async () => {
      const allFonts = await getAllFonts();
      console.warn("All fonts detected", allFonts);
      setDetectedFonts(allFonts);
    };
    gatherAllFonts();
  }, []);

  // §9.2 — the editor panel route bypasses Startup, so pull the current
  // stylesheet from the host on mount. No-op outside the cross-origin embed.
  useEffect(() => {
    if (!isHostPanel) return undefined;
    let cancelled = false;
    requestStyleSheetFromParent()
      .then((payload) => {
        if (!cancelled && payload) dispatch(loadSheets(payload));
      })
      .catch((e) => console.warn("stylesheet pull from host failed", e));
    return () => {
      cancelled = true;
    };
  }, [isHostPanel, dispatch]);

  // §9.3 — stream draft edits to the host for live preview, debounced. The
  // effective sheet is the draft over the saved copy. No-op outside the embed.
  useEffect(() => {
    if (!isHostPanel) return undefined;
    const postDraft = debounce(() => {
      const s = store.getState().styleSheet;
      const keys = new Set([...Object.keys(s.sheets), ...Object.keys(s.draftSheets)]);
      const sheets = {};
      keys.forEach((k) => {
        const effective = s.draftSheets[k] ?? s.sheets[k];
        if (effective) sheets[k] = effective;
      });
      window.parent.postMessage(
        { type: "gridd-stylesheet-draft", payload: { selectedSheet: s.selectedSheet, sheets } },
        "*"
      );
    }, 250);
    let prev = store.getState().styleSheet;
    const unsubscribe = store.subscribe(() => {
      const next = store.getState().styleSheet;
      if (
        next.sheets !== prev.sheets ||
        next.draftSheets !== prev.draftSheets ||
        next.selectedSheet !== prev.selectedSheet
      ) {
        prev = next;
        postDraft();
      }
    });
    return () => {
      unsubscribe();
      postDraft.cancel();
    };
  }, [isHostPanel, store]);

  const factorySheets = ["Default", "Red", "Green", "Black"];

  return openWizard ? (
    detectedColors.length > 0 && detectedFonts.length > 0 ? (
      <StyleSheetWizard
        detectedFonts={detectedFonts}
        availableFonts={selectedSheet?.fontFamilies}
        detectedColors={detectedColors}
        existingColors={selectedSheet?.colors}
        selectedSheetKey={selectedSheetKey}
        onResult={(result) => {
          dispatch(closeStyleSheetWizard());

          if (result.choice === "customize") {
            setSelectedSheetKey(result.suggestedSheetKey);
          }

          if (result.choice === "choose") {
            setOpenHamburger(true);
          }

          if (result.choice === "set") {
            console.warn("auto close stylesheet editor");
            dispatch(applyChangesOnAll());
            dispatch(setSelectedSheet({ sheetName: result.suggestedSheetKey }));
            dispatch(storeSheets({ to: "localStorage" }));
            dispatch(storeSheets({ to: "presentation" }));
            dispatch(closeStyleSheetDialog());
          }
        }}
      />
    ) : (
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 12, fontColor: "black" }}>
          {t("wizard.loading")}
        </div>
        <Spinner />
      </div>
    )
  ) : (
    <div
      id="stylesheet_container"
      className={isPanelVariant ? classes.PanelRoot : undefined}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <div style={{ flex: "1 1 auto", display: "flex", alignItems: "center" }}>
          <Menu open={openHamburger} onOpenChange={(_, d) => setOpenHamburger(d.open)}>
            <MenuTrigger>
              <Button
                id="style_sheet_menu_button"
                appearance="subtle"
                icon={<Hamburger style={{ height: 16, width: 16 }} />}
                style={{ fontSize: 16 }}
                onClick={() => setOpenHamburger(true)}
              >
                {t("menu.stylesheet.title")}
              </Button>
            </MenuTrigger>

            <MenuPopover id="style_sheet_menu_popover">
              <MenuList>
                <CommonTooltip content={t("stylesheet.new.tooltip")}>
                  <MenuItem
                    icon={<Add16Filled />}
                    onClick={() => {
                      dispatch(updateSheet({ sheetKey: "new" }));
                      setSelectedSheetKey("new");
                      setRenaming(false);
                      setNewName(null);
                      if (props.onSelectedKeyChange) props.onSelectedKeyChange("new");
                    }}
                  >
                    {t("stylesheet.new")}
                  </MenuItem>
                </CommonTooltip>

                {Object.keys(styleSheet.draftSheets)
                  .filter((draftKey) => Object.keys(styleSheet.sheets).findIndex((key) => key === draftKey) === -1)
                  .sort()
                  .map((draftKey) => (
                    <MenuItem
                      key={draftKey}
                      id={draftKey}
                      value={draftKey}
                      onClick={() => {
                        setSelectedSheetKey(draftKey);
                        setRenaming(false);
                        setNewName(null);
                        if (props.onSelectedKeyChange) props.onSelectedKeyChange(draftKey);
                      }}
                    >
                      {selectedSheetKey === draftKey ? (
                        <Checkmark20Filled className={classes.TabHeaderCheckMark} />
                      ) : (
                        <></>
                      )}
                      <Text italic={styleSheet.draftSheets[draftKey] !== undefined}>{draftKey}</Text>
                    </MenuItem>
                  ))}

                {Object.keys(styleSheet.sheets)
                  .filter((key) => styleSheet.sheets[key] !== undefined)
                  .sort((a) => (factorySheets.includes(a) ? 1 : -1))
                  .map((key) =>
                    key === "default" ? (
                      <React.Fragment key={key}>
                        <Divider />
                        <MenuItem
                          key={key}
                          id={key}
                          value={key}
                          onClick={() => {
                            setSelectedSheetKey(key);
                            setRenaming(false);
                            setNewName(null);
                            if (props.onSelectedKeyChange) props.onSelectedKeyChange(key);
                          }}
                        >
                          {selectedSheetKey === key ? (
                            <Checkmark20Filled className={classes.TabHeaderCheckMark} />
                          ) : (
                            <></>
                          )}
                          <Text italic={styleSheet.draftSheets[key] !== undefined} style={{ marginRight: 4 }}>
                            {key === "default" ? t("stylesheet." + key) : key}
                          </Text>
                        </MenuItem>
                      </React.Fragment>
                    ) : (
                      <MenuItem
                        key={key}
                        id={key}
                        value={key}
                        onClick={() => {
                          setSelectedSheetKey(key);
                          setRenaming(false);
                          setNewName(null);
                          if (props.onSelectedKeyChange) props.onSelectedKeyChange(key);
                        }}
                      >
                        {selectedSheetKey === key ? (
                          <Checkmark20Filled className={classes.TabHeaderCheckMark} />
                        ) : (
                          <></>
                        )}
                        <Text italic={styleSheet.draftSheets[key] !== undefined} style={{ marginRight: 4 }}>
                          {key === "default" ? t("stylesheet." + key) : key}
                        </Text>
                      </MenuItem>
                    )
                  )}
              </MenuList>
            </MenuPopover>
          </Menu>
          {renaming ? (
            <div>
              <Input defaultValue={selectedSheetKey} onChange={(ev, data) => setNewName(data.value)} />
              <Button
                icon={<Save20Filled />}
                onClick={() => {
                  dispatch(rename({ sheetKey: selectedSheetKey, to: newName }));
                  setSelectedSheetKey(newName);
                }}
              />
              <Button
                icon={<ArrowExit20Filled />}
                onClick={() => {
                  setNewName(null);
                  setRenaming(false);
                }}
              />
            </div>
          ) : (
            <div>
              <Label className={classes.SelectedSheetTitle}>
                {selectedSheetKey === "default" ? t("stylesheet.default") : selectedSheetKey}
              </Label>
            </div>
          )}
        </div>
        {!renaming ? (
          <Menu>
            <MenuTrigger>
              <Button
                id="stylesheet_settings"
                icon={<GearIcon width={16} height={16} />}
                size="small"
                style={{
                  float: "right",
                }}
                appearance="subtle"
              />
            </MenuTrigger>

            <MenuPopover>
              <CommonTooltip content={t("stylesheet.rename.tooltip")}>
                <MenuItem
                  icon={<StylesheetRename />}
                  onClick={() => setRenaming(true)}
                  disabled={selectedSheetKey === "default"}
                >
                  {t("stylesheet.rename")}
                </MenuItem>
              </CommonTooltip>
              <CommonTooltip content={t("stylesheet.wizard.tooltip")}>
                <MenuItem
                  icon={<Wand />}
                  onClick={() => {
                    dispatch(openStyleSheetWizard());
                  }}
                  disabled={!exists("PowerPoint")}
                >
                  {t("stylesheet.wizard")}
                </MenuItem>
              </CommonTooltip>
              <CommonTooltip content={t("stylesheet.copy.tooltip")}>
                <MenuItem
                  icon={<StylesheetDuplicate />}
                  onClick={() => {
                    dispatch(updateSheet({ sheetKey: selectedSheetKey + "_copy", from: selectedSheetKey }));
                  }}
                >
                  {t("stylesheet.duplicate")}
                </MenuItem>
              </CommonTooltip>

              <CommonTooltip content={t("stylesheet.reset.factory")}>
                <MenuItem
                  id="reset_stylesheet"
                  icon={<StylesheetReset />}
                  onClick={() => {
                    dispatch(resetFromDefault({ sheetKey: selectedSheetKey }));
                    notify("success", "success.stylesheet.reset");
                  }}
                >
                  {t("stylesheet.reset")}
                </MenuItem>
              </CommonTooltip>

              <CommonTooltip content={t("stylesheet.import.tooltip")}>
                <MenuItem
                  icon={<Import />}
                  onClick={async () => {
                    let styleSheet;
                    try {
                      const content = await navigator.clipboard.read();
                      for (const item of content) {
                        const blob = await item.getType("text/plain");
                        if (blob) {
                          const text = await blob.text();
                          if (text) {
                            styleSheet = text;
                            break;
                          }
                        }
                      }
                      const styleSheetObject = JSON.parse(styleSheet);
                      if (styleSheetObject.presets === undefined) throw Error("error.paste.stylesheet");
                      dispatch(importSheet({ sheetKey: selectedSheetKey, sheet: styleSheetObject }));
                      notify("success", "success.paste.stylesheet");
                    } catch (error) {
                      console.error("error importing stylesheet", { error, styleSheet });
                      notify("error", error.message);
                    }
                  }}
                >
                  {t("stylesheet.import")}
                </MenuItem>
              </CommonTooltip>

              <CommonTooltip content={t("stylesheet.export.tooltip")}>
                <MenuItem
                  icon={<StylesheetExport />}
                  onClick={() => {
                    const stringified = JSON.stringify(selectedSheet);
                    const blobbed = new Blob([stringified], { type: "text/plain" });
                    navigator.clipboard.write([new ClipboardItem({ "text/plain": blobbed })]);
                    notify("success", "success.copy.stylesheet");
                  }}
                >
                  {t("stylesheet.export")}
                </MenuItem>
              </CommonTooltip>

              {selectedSheetKey !== "default" ? (
                <CommonTooltip content={t("stylesheet.delete.tooltip")}>
                  <MenuItem
                    icon={<Delete />}
                    onClick={() => {
                      setSelectedSheetKey("default");
                      setRenaming(false);
                      setNewName(null);
                      dispatch(deleteSheet({ sheetKey: selectedSheetKey }));
                      if (props.onSelectedKeyChange) props.onSelectedKeyChange("default");
                    }}
                  >
                    {t("stylesheet.delete")}
                  </MenuItem>
                </CommonTooltip>
              ) : (
                <></>
              )}

              <CommonTooltip content={t("stylesheet.cleanup.tooltip")}>
                <MenuItem
                  icon={<PaintBrushIcon />}
                  onClick={() => {
                    setAllFonts(getBodyPreset(selectedSheet).fontFamily);
                  }}
                >
                  {t("stylesheet.cleanup")}
                </MenuItem>
              </CommonTooltip>
            </MenuPopover>
          </Menu>
        ) : (
          <></>
        )}
      </div>

      <div
        style={{
          display: "grid",
          // Panel mode drops the left quick-travel column and goes single-column.
          gridTemplateColumns: isPanelVariant ? "1fr" : "auto 1fr",
          justifyContent: "center",
          flex: "1 1 auto",
          overflow: "hidden",
        }}
      >
        {!isPanelVariant && (
          <div style={{ width: 150 }}>
            <div
              style={{
                padding: 4,
                marginTop: 20,
                marginRight: 4,
                display: "flex",
                flexFlow: "column",
                gap: 8,
                background: "#FFFFFF",
                borderRadius: 12,
                height: "fit-content",
                justifyItems: "center",
                position: "sticky",
                top: 0,
              }}
            >
              <TabList size="small" vertical={true} selectedValue={lastScrolledTab}>
                <Tab
                  id="presets"
                  value={"presets"}
                  onClick={() => presetsRef?.current?.scrollIntoView(scrollOptions)}
                  icon={<StylesheetFonts />}
                  style={{ fontWeight: lastScrolledTab === "presets" ? "bold" : "normal" }}
                >
                  {t("stylesheet.nav.presets")}
                </Tab>
                <Tab
                  id="colors"
                  value={"colors"}
                  onClick={() => colorsRef?.current?.scrollIntoView(scrollOptions)}
                  icon={<StylesheetColors />}
                  style={{ fontWeight: lastScrolledTab === "colors" ? "bold" : "normal" }}
                >
                  {t("stylesheet.nav.colors")}
                </Tab>
                <Tab
                  id="spacing"
                  value={"spacing"}
                  onClick={() => spacingRef?.current?.scrollIntoView(scrollOptions)}
                  icon={<StylesheetSpacing />}
                  style={{ fontWeight: lastScrolledTab === "spacing" ? "bold" : "normal" }}
                >
                  {t("stylesheet.nav.spacing")}
                </Tab>
                <Tab
                  id="borders"
                  value={"borders"}
                  onClick={() => bordersRef?.current?.scrollIntoView(scrollOptions)}
                  icon={<StylesheetBorders />}
                  style={{ fontWeight: lastScrolledTab === "borders" ? "bold" : "normal" }}
                >
                  {t("stylesheet.nav.borders")}
                </Tab>
                <Tab
                  id="spacers"
                  value={"spacers"}
                  onClick={() => spacersRef?.current?.scrollIntoView(scrollOptions)}
                  icon={<StylesheetSpacers />}
                  style={{ fontWeight: lastScrolledTab === "spacers" ? "bold" : "normal" }}
                >
                  {t("stylesheet.nav.spacers")}
                </Tab>
                {selectedSheet?.shapePresets?.length > 0 && (
                  <Tab
                    id="shapePresets"
                    value={"shapePresets"}
                    onClick={() => shapePresetsRef?.current?.scrollIntoView(scrollOptions)}
                    icon={<ShapesMenu />}
                    style={{ fontWeight: lastScrolledTab === "shapePresets" ? "bold" : "normal" }}
                  >
                    {t("stylesheet.nav.shapePresets")}
                  </Tab>
                )}
              </TabList>
            </div>
          </div>
        )}

        <div
          style={{
            height: "100%",
            width: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
          onScroll={() => {
            let closestToTopVisible = null;
            const presetsTop = presetsRef?.current?.getBoundingClientRect()?.top;
            const colorsTop = colorsRef?.current?.getBoundingClientRect()?.top;
            const spacingTop = spacingRef?.current?.getBoundingClientRect()?.top;
            const bordersTop = bordersRef?.current?.getBoundingClientRect()?.top;
            const spacersTop = spacersRef?.current?.getBoundingClientRect()?.top;
            const shapePresetsTop = shapePresetsRef?.current?.getBoundingClientRect()?.top;
            if (presetsTop > 0) closestToTopVisible = "presets";
            else if (colorsTop > 0) closestToTopVisible = "colors";
            else if (spacingTop > 0) closestToTopVisible = "spacing";
            else if (bordersTop > 0) closestToTopVisible = "borders";
            else if (spacersTop > 0) closestToTopVisible = "spacers";
            else if (shapePresetsTop > 0) closestToTopVisible = "shapePresets";
            setLastScrolledTab(closestToTopVisible);
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              margin: 24,
            }}
          >
            <div ref={presetsRef} style={{ background: "#F8F8F8", borderRadius: 16, padding: 8, paddingLeft: 16 }}>
              <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
                <StylesheetFonts />
                {t("stylesheet.nav.presets")}
              </div>
              <div
                style={{
                  // Panel mode stacks each preset as a two-row block; the flat
                  // six-column grid (with its header row) is the wide layout.
                  display: isPanelVariant ? "flex" : "grid",
                  flexDirection: isPanelVariant ? "column" : undefined,
                  gridTemplateColumns: isPanelVariant ? undefined : "repeat(6, 1fr)",
                  gridTemplateRows: isPanelVariant
                    ? undefined
                    : "repeat(" + ((selectedSheet?.presets?.length ?? 1) + 1) + ", auto)",
                  rowGap: 4,
                }}
              >
                {!isPanelVariant && (
                  <>
                    <Label key={"stylesheet_presets_name"}>{t("stylesheet.presets.name")}</Label>
                    <Label key={"stylesheet_presets_font_families"}>{t("stylesheet.presets.font_families")}</Label>
                    <Label key={"stylesheet_presets_relative_size"}>{t("stylesheet.presets.relative_size")}</Label>
                    <Label key={"stylesheet_presets_style"}>
                      {t("stylesheet.presets.style")}{" "}
                      <CommonTooltip content={t("stylesheet.presets.style.info")} showDelay={500}>
                        <InfoIcon width={20} height={20} style={{ verticalAlign: "middle" }} />
                      </CommonTooltip>
                    </Label>
                    <Label key={"stylesheet_presets_color"}>{t("stylesheet.presets.color")}</Label>
                    <Label key={"stylesheet_presets_color2"}>{t("stylesheet.presets.color2")}</Label>
                  </>
                )}

                {renderPresetBlock(
                  "body",
                  <div key={"body_preview"}>
                    <Label
                      style={{
                        fontFamily: getBodyPreset(selectedSheet)?.fontFamily ?? "Arial",
                        fontWeight: getBodyPreset(selectedSheet)?.fontWeight,
                        fontStyle: getBodyPreset(selectedSheet)?.fontStyle,
                        fontSize: getBodyPreset(selectedSheet)?.fontSize ?? "12pt",
                        textDecoration: getBodyPreset(selectedSheet)?.fontUnderlined ? "underline" : undefined,
                        color:
                          selectedSheet?.colors?.find((c) => c.name === getBodyPreset(selectedSheet).fontColor)
                            ?.value ?? "black",
                      }}
                    >
                      {getBodyPreset(selectedSheet)?.name}
                    </Label>
                  </div>,
                  <>
                    <div key={"body_font"}>
                      <Dropdown
                        id="body_font"
                        size="small"
                        style={{ minWidth: 0 }}
                        value={getBodyPreset(selectedSheet)?.fontFamily}
                        onOptionSelect={(_, data) => {
                          dispatch(
                            updateSheet({
                              sheetKey: selectedSheetKey,
                              preset: {
                                name: getBodyPreset(selectedSheet)?.name,
                                fontFamily: data.optionValue,
                              },
                            })
                          );
                        }}
                      >
                        {selectedSheet?.fontFamilies?.map((ff) => (
                          <Option key={ff} id={ff} value={ff} text={ff} style={{ fontFamily: ff }}>
                            {ff}
                          </Option>
                        ))}
                      </Dropdown>
                    </div>

                    <div key={"body_size"}>
                      <Dropdown
                        id="body_size"
                        size="small"
                        style={{ minWidth: 0 }}
                        value={getBodyPreset(selectedSheet)?.fontSize}
                        onOptionSelect={(_, data) => {
                          dispatch(
                            updateSheet({
                              sheetKey: selectedSheetKey,
                              preset: {
                                name: getBodyPreset(selectedSheet)?.name,
                                fontSize: data.optionValue,
                              },
                            })
                          );
                        }}
                      >
                        {selectedSheet?.fontSizes?.map((f) => (
                          <Option key={"fontSize" + f} id={"fontSize" + f} value={f} text={f}>
                            {f}
                          </Option>
                        ))}
                      </Dropdown>
                    </div>

                    <div key={"body_style"}>
                      {/* <ToggleButton
                    id="body_bold"
                    icon={<TextBold20Filled />}
                    appearance="subtle"
                    checked={getBodyPreset(selectedSheet)?.fontWeight === "bold"}
                    onClick={() => {
                      dispatch(
                        updateSheet({
                          sheetKey: selectedSheetKey,
                          preset: {
                            name: getBodyPreset(selectedSheet)?.name,
                            fontWeight: getBodyPreset(selectedSheet)?.fontWeight === "bold" ? "normal" : "bold",
                          },
                        })
                      );
                    }}
                  />

                  <ToggleButton
                    id="body_italic"
                    icon={<TextItalic20Filled />}
                    appearance="subtle"
                    checked={getBodyPreset(selectedSheet)?.fontStyle === "italic"}
                    onClick={() => {
                      dispatch(
                        updateSheet({
                          sheetKey: selectedSheetKey,
                          preset: {
                            name: getBodyPreset(selectedSheet)?.name,
                            fontStyle: getBodyPreset(selectedSheet)?.fontStyle === "italic" ? "normal" : "italic",
                          },
                        })
                      );
                    }}
                  />

                  <ToggleButton
                    id="body_underline"
                    icon={<TextUnderline20Filled />}
                    appearance="subtle"
                    checked={getBodyPreset(selectedSheet)?.textDecoration === "underline"}
                    onClick={() => {
                      dispatch(
                        updateSheet({
                          sheetKey: selectedSheetKey,
                          preset: {
                            name: getBodyPreset(selectedSheet)?.name,
                            textDecoration:
                              getBodyPreset(selectedSheet)?.textDecoration === "underline" ? "normal" : "underline",
                          },
                        })
                      );
                    }}
                  /> */}
                    </div>

                    <div key={"body_color_1"}>
                      <Dropdown
                        id="body_color_1"
                        size="small"
                        style={{ minWidth: 0 }}
                        value={t("menu.style.colors." + getBodyPreset(selectedSheet)?.fontColor)}
                        onOptionSelect={(_, data) => {
                          dispatch(
                            updateSheet({
                              sheetKey: selectedSheetKey,
                              preset: {
                                name: getBodyPreset(selectedSheet)?.name,
                                fontColor: data.optionValue,
                              },
                            })
                          );
                        }}
                      >
                        {selectedSheet?.colors?.map((c) => (
                          <Option key={c.name} id={c.name} value={c.name} text={c.name}>
                            <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: 4 }}>
                              <div
                                style={{
                                  width: 16,
                                  height: 16,
                                  border: "1px solid black",
                                  background: selectedSheet?.colors?.find((sc) => sc.name === c.name)?.value ?? "black",
                                }}
                              ></div>
                              <Label>{t("menu.style.colors." + c.name)}</Label>
                            </div>
                          </Option>
                        ))}
                      </Dropdown>
                    </div>

                    <div key={"body_color_2"}>
                      <Dropdown
                        id="body_color_2"
                        size="small"
                        style={{ minWidth: 0 }}
                        value={t("menu.style.colors." + getBodyPreset(selectedSheet)?.fontColor2)}
                        onOptionSelect={(_, data) => {
                          dispatch(
                            updateSheet({
                              sheetKey: selectedSheetKey,
                              preset: {
                                name: getBodyPreset(selectedSheet).name,
                                fontColor2: data.optionValue,
                              },
                            })
                          );
                        }}
                      >
                        {selectedSheet?.colors?.map((c) => (
                          <Option key={c.name} id={c.name} value={c.name} text={c.name}>
                            <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: 4 }}>
                              <div
                                style={{
                                  width: 16,
                                  height: 16,
                                  border: "1px solid black",
                                  background: selectedSheet?.colors?.find((sc) => sc.name === c.name)?.value ?? "black",
                                }}
                              ></div>
                              <Label>{t("menu.style.colors." + c.name)}</Label>
                            </div>
                          </Option>
                        ))}
                      </Dropdown>
                    </div>
                  </>
                )}

                {selectedSheet?.presets
                  ?.filter((p) => p.name !== "Body")
                  ?.map((preset) => {
                    const diff =
                      parseInt(preset?.fontSize?.replace("pt", "")) -
                      parseInt(getBodyPreset(selectedSheet)?.fontSize?.replace("pt", ""));
                    return renderPresetBlock(
                      preset.name,
                      <div key={preset.name + "_preview"}>
                        <Label
                          style={{
                            fontFamily: preset.fontFamily ?? "Arial",
                            fontWeight: preset.fontWeight,
                            fontStyle: preset.fontStyle,
                            fontSize: preset.fontSize ?? "12pt",
                            textDecoration: preset.fontUnderlined ? "underline" : undefined,
                            color: selectedSheet?.colors?.find((c) => c.name === preset.fontColor)?.value ?? "black",
                          }}
                        >
                          {t("menu.style.presets." + preset.name)}
                        </Label>
                      </div>,
                      <>
                        <div key={preset.name + "_font"}>
                          <Dropdown
                            id={`${preset.name.toLowerCase()}_font`}
                            size="small"
                            style={{ minWidth: 0 }}
                            value={preset.fontFamily}
                            onOptionSelect={(_, data) => {
                              dispatch(
                                updateSheet({
                                  sheetKey: selectedSheetKey,
                                  preset: {
                                    name: preset.name,
                                    fontFamily: data.optionValue,
                                  },
                                })
                              );
                            }}
                          >
                            {selectedSheet?.fontFamilies?.map((ff) => (
                              <Option key={ff} id={ff} value={ff} text={ff} style={{ fontFamily: ff }}>
                                {ff}
                              </Option>
                            ))}
                          </Dropdown>
                        </div>

                        <div key={preset.name + "_size"}>
                          <Button
                            size="small"
                            id={`${preset.name.toLowerCase()}_size_decrease`}
                            icon={<Subtract16Filled />}
                            appearance="subtle"
                            onClick={() => {
                              dispatch(
                                updateSheet({
                                  sheetKey: selectedSheetKey,
                                  preset: {
                                    name: preset.name,
                                    fontSize: `${parseInt(preset.fontSize.replace("pt", "")) - 1}pt`,
                                  },
                                })
                              );
                            }}
                          />
                          <Label
                            id={`${preset.name.toLowerCase()}_size`}
                            style={{ display: "inline-flex", minWidth: 36 }}
                          >
                            {diff > 0 ? "+" : ""}
                            {diff} pt
                          </Label>
                          <Button
                            size="small"
                            id={`${preset.name.toLowerCase()}_size_increase`}
                            icon={<Add16Filled />}
                            appearance="subtle"
                            onClick={() => {
                              dispatch(
                                updateSheet({
                                  sheetKey: selectedSheetKey,
                                  preset: {
                                    name: preset.name,
                                    fontSize: `${parseInt(preset.fontSize.replace("pt", "")) + 1}pt`,
                                  },
                                })
                              );
                            }}
                          />
                        </div>

                        <div key={preset.name + "_style"}>
                          <ToggleButton
                            id={`${preset.name.toLowerCase()}_bold`}
                            icon={<TextBold20Filled />}
                            appearance="subtle"
                            checked={preset.fontWeight === "bold"}
                            onClick={() => {
                              dispatch(
                                updateSheet({
                                  sheetKey: selectedSheetKey,
                                  preset: {
                                    name: preset.name,
                                    fontWeight: preset.fontWeight === "bold" ? "normal" : "bold",
                                  },
                                })
                              );
                            }}
                          ></ToggleButton>

                          <ToggleButton
                            id={`${preset.name.toLowerCase()}_italic`}
                            icon={<TextItalic20Filled />}
                            appearance="subtle"
                            checked={preset.fontStyle === "italic"}
                            onClick={() => {
                              dispatch(
                                updateSheet({
                                  sheetKey: selectedSheetKey,
                                  preset: {
                                    name: preset.name,
                                    fontStyle: preset.fontStyle === "italic" ? "normal" : "italic",
                                  },
                                })
                              );
                            }}
                          ></ToggleButton>

                          <ToggleButton
                            id={`${preset.name.toLowerCase()}_underline`}
                            icon={<TextUnderline20Filled />}
                            appearance="subtle"
                            checked={preset.fontUnderlined}
                            onClick={() => {
                              dispatch(
                                updateSheet({
                                  sheetKey: selectedSheetKey,
                                  preset: {
                                    name: preset.name,
                                    fontUnderlined: !preset.fontUnderlined,
                                  },
                                })
                              );
                            }}
                          ></ToggleButton>
                        </div>

                        <div key={preset.name + "_color_1"}>
                          <Dropdown
                            id={`${preset.name.toLowerCase()}_color_1`}
                            size="small"
                            style={{ minWidth: 0 }}
                            value={t("menu.style.colors." + getPreset(preset.name).fontColor)}
                            onOptionSelect={(_, data) => {
                              dispatch(
                                updateSheet({
                                  sheetKey: selectedSheetKey,
                                  preset: {
                                    name: preset.name,
                                    fontColor: data.optionValue,
                                  },
                                })
                              );
                            }}
                          >
                            {selectedSheet?.colors?.map((c) => (
                              <Option key={c.name} id={c.name} value={c.name} text={c.name}>
                                <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: 4 }}>
                                  <div
                                    style={{
                                      width: 16,
                                      height: 16,
                                      border: "1px solid black",
                                      background:
                                        selectedSheet?.colors?.find((sc) => sc.name === c.name)?.value ?? "black",
                                    }}
                                  ></div>
                                  <Label>{t("menu.style.colors." + c.name)}</Label>
                                </div>
                              </Option>
                            ))}
                          </Dropdown>
                        </div>

                        <div key={preset.name + "_color_2"}>
                          <Dropdown
                            id={`${preset.name.toLowerCase()}_color_2`}
                            size="small"
                            style={{ minWidth: 0 }}
                            value={t("menu.style.colors." + getPreset(preset.name).fontColor2)}
                            onOptionSelect={(_, data) => {
                              dispatch(
                                updateSheet({
                                  sheetKey: selectedSheetKey,
                                  preset: {
                                    name: preset.name,
                                    fontColor2: data.optionValue,
                                  },
                                })
                              );
                            }}
                          >
                            {selectedSheet?.colors?.map((c) => (
                              <Option key={c.name} id={c.name} value={c.name} text={c.name}>
                                <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: 4 }}>
                                  <div
                                    style={{
                                      width: 16,
                                      height: 16,
                                      border: "1px solid black",
                                      background:
                                        selectedSheet?.colors?.find((sc) => sc.name === c.name)?.value ?? "black",
                                    }}
                                  ></div>
                                  <Label>{t("menu.style.colors." + c.name)}</Label>
                                </div>
                              </Option>
                            ))}
                          </Dropdown>
                        </div>
                      </>
                    );
                  })}
              </div>
            </div>

            <div
              ref={colorsRef}
              style={{ background: "#F8F8F8", borderRadius: 16, padding: 8, paddingLeft: 16, marginTop: 32 }}
            >
              <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
                <StylesheetColors />
                {t("stylesheet.nav.colors")}
              </div>
              <div
                style={{
                  gap: 12,
                  alignItems: "start",
                  display: "grid",
                  // Panel mode stacks all colors in one column.
                  gridTemplateColumns: isPanelVariant ? "1fr" : "1fr 1fr 1fr",
                }}
              >
                {selectedSheet?.colors?.map((color, i) => (
                  <div
                    key={color.name}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "100px auto 1fr",
                      // Explicit placement builds the three columns; in panel mode
                      // let items flow naturally down the single column.
                      ...(isPanelVariant
                        ? {}
                        : {
                            gridColumn: i < 12 ? 1 : i < 22 ? 2 : 3,
                            gridRow: i < 12 ? i + 1 : i < 22 ? i - 12 + 1 : i - 22 + 1,
                          }),
                    }}
                  >
                    {t("menu.style.colors." + color.name)}:
                    <Menu
                      open={openMenus[`color-${color.name}`] || false}
                      onOpenChange={(e, data) => {
                        setOpenMenus((prev) => ({ ...prev, [`color-${color.name}`]: data.open }));
                        if (data.open) {
                          dispatch(openColorPicker());
                        } else {
                          dispatch(closeColorPicker());
                        }
                      }}
                    >
                      <MenuTrigger>
                        <Button icon={<CaretDownIcon />} iconPosition="after" style={{ minWidth: 0 }} size="small">
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 2,
                              backgroundColor: color.value ?? "black",
                            }}
                          />
                        </Button>
                      </MenuTrigger>
                      <MenuPopover style={{ minWidth: "350px", maxWidth: "400px" }}>
                        <CustomColorPickerSubMenu
                          color={color.value}
                          detectedColors={detectedColors}
                          onNewColor={(newColor) => {
                            dispatch(
                              updateSheet({
                                sheetKey: selectedSheetKey,
                                color: { name: color.name, value: newColor },
                              })
                            );
                            const updatedShapeSettings = { ...lastShapeSettings };
                            const updatedIconSettings = { ...lastIconSettings };

                            if (color.name === lastShapeSettings?.colorName) {
                              updatedShapeSettings.color = newColor;
                            }
                            if (color.name === lastShapeSettings?.outlineColorName) {
                              updatedShapeSettings.outlineColor = newColor;
                            }
                            dispatch(
                              storeLastSetting({
                                shape: updatedShapeSettings,
                              })
                            );
                            if (color.name === lastIconSettings?.iconColorName) {
                              updatedIconSettings.iconColor = newColor;
                              dispatch(
                                storeLastSetting({
                                  icon: updatedIconSettings,
                                })
                              );
                            }
                          }}
                          onClose={() => closeColorPickerMenu(`color-${color.name}`)}
                        />
                      </MenuPopover>
                    </Menu>
                  </div>
                ))}
              </div>
            </div>

            <div
              ref={spacingRef}
              style={{ background: "#F8F8F8", borderRadius: 16, padding: 8, paddingLeft: 16, marginTop: 32 }}
            >
              <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
                <StylesheetSpacing />
                {t("stylesheet.nav.spacing")}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexFlow: "wrap",
                  maxWidth: "400px",
                  gap: 12,
                }}
              >
                <Label>{t("stylesheet.other.cell-padding")}</Label>
                <Input
                  size="small"
                  style={{
                    minWidth: 64,
                    width: 64,
                  }}
                  defaultValue={selectedSheet?.cellPadding}
                  onChange={(e, data) => {
                    dispatch(updateSheet({ sheetKey: selectedSheetKey, cellPadding: data.value }));
                  }}
                />
                <Label>{t("stylesheet.other.cell-padding.px")}</Label>
              </div>

              <LineSpacingTable selectedSheetKey={selectedSheetKey} />
            </div>

            <div
              ref={bordersRef}
              style={{ background: "#F8F8F8", borderRadius: 16, padding: 8, paddingLeft: 16, marginTop: 32 }}
            >
              <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
                <StylesheetBorders />
                {t("stylesheet.nav.borders")}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexFlow: "wrap",
                  maxWidth: "400px",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto repeat(3, 1fr)",
                    gridTemplateRows: "1fr " + selectedSheet?.presets?.map(() => "1fr "),
                    rowGap: 4,
                  }}
                >
                  <div />
                  <Label>{t("stylesheet.borders.color")}</Label>
                  <Label>{t("stylesheet.borders.type")}</Label>
                  <Label>{t("stylesheet.borders.weight")}</Label>

                  {selectedSheet?.borders?.map((border) => (
                    <>
                      <div
                        key={border.name + "_preview"}
                        style={{
                          marginRight: 12,
                        }}
                      >
                        <div
                          style={{
                            borderBottomColor:
                              selectedSheet?.colors?.find((c) => c.name === border.color)?.value ?? "black",
                            borderBottomWidth: border.width,
                            borderBottomStyle: border.type,
                          }}
                        >
                          {t("menu.style.borders." + border.name)}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          maxWidth: "200px",
                        }}
                      >
                        <Dropdown
                          size="small"
                          style={{ minWidth: 0 }}
                          value={border.color}
                          onOptionSelect={(_, data) => {
                            dispatch(
                              updateSheet({
                                sheetKey: selectedSheetKey,
                                border: {
                                  name: border.name,
                                  color: data.optionValue,
                                },
                              })
                            );
                          }}
                        >
                          {selectedSheet?.colors?.map((c) => (
                            <Option key={c.name} id={c.name} value={c.name} text={c.name}>
                              <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: 4 }}>
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    border: "1px solid black",
                                    background:
                                      selectedSheet?.colors?.find((sc) => sc.name === c.name)?.value ?? "black",
                                  }}
                                ></div>
                                <Label>{t("menu.style.colors." + c.name)}</Label>
                              </div>
                            </Option>
                          ))}
                        </Dropdown>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          maxWidth: "200px",
                        }}
                      >
                        <Dropdown
                          size="small"
                          style={{ minWidth: 0 }}
                          value={border.type}
                          onOptionSelect={(_, data) => {
                            dispatch(
                              updateSheet({
                                sheetKey: selectedSheetKey,
                                border: {
                                  name: border.name,
                                  type: data.optionValue,
                                },
                              })
                            );
                          }}
                        >
                          <Option key={"solid"} id={"solid"} value={"solid"} text={"solid"}>
                            <Label>{t("menu.border.type.solid")}</Label>
                          </Option>
                          <Option key={"dashed"} id={"dashed"} value={"dashed"} text={"dashed"}>
                            <Label>{t("menu.border.type.dashed")}</Label>
                          </Option>
                          <Option key={"dotted"} id={"dotted"} value={"dotted"} text={"dotted"}>
                            <Label>{t("menu.border.type.dotted")}</Label>
                          </Option>
                        </Dropdown>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          maxWidth: "200px",
                        }}
                      >
                        <Dropdown
                          size="small"
                          style={{ minWidth: 0 }}
                          value={border.width}
                          onOptionSelect={(_, data) => {
                            dispatch(
                              updateSheet({
                                sheetKey: selectedSheetKey,
                                border: {
                                  name: border.name,
                                  width: data.optionValue,
                                },
                              })
                            );
                          }}
                        >
                          <Option key={"thin"} id={"thin"} value={1} text={1}>
                            <LineTypeSolid style={{ height: 10 }} />
                            <Label>{t("menu.border.weight.thin")}</Label>
                          </Option>
                          <Option key={"normal"} id={"normal"} value={2} text={2}>
                            <LineTypeSolid style={{ height: 14 }} />
                            <Label>{t("menu.border.weight.normal")}</Label>
                          </Option>
                          <Option key={"thick"} id={"thick"} value={3} text={3}>
                            <LineTypeSolid style={{ height: 18 }} />
                            <Label>{t("menu.border.weight.thick")}</Label>
                          </Option>
                          <Option key={"extrathick"} id={"extrathick"} value={4} text={4}>
                            <LineTypeSolid style={{ height: 22 }} />
                            <Label>{t("menu.border.weight.extrathick")}</Label>
                          </Option>
                        </Dropdown>
                      </div>
                    </>
                  ))}
                </div>
              </div>
            </div>

            <div
              ref={spacersRef}
              style={{ background: "#F8F8F8", borderRadius: 16, padding: 8, paddingLeft: 16, marginTop: 32 }}
            >
              <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
                <StylesheetSpacers />
                {t("stylesheet.nav.spacers")}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexFlow: "wrap",
                  maxWidth: "400px",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gridTemplateRows: "1fr " + selectedSheet?.presets?.map(() => "1fr "),
                    rowGap: 4,
                  }}
                >
                  <div />
                  <Label>{t("stylesheet.spacers.width")}</Label>

                  {selectedSheet?.spacerWidths?.map((sw) => (
                    <>
                      <div
                        key={sw.name + "_name"}
                        style={{
                          marginRight: 12,
                        }}
                      >
                        {t("menu.style.spacers." + sw.name)}
                      </div>
                      <div
                        key={sw.name + "_fields"}
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          flexFlow: "wrap",
                          gap: 4,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                            maxWidth: "200px",
                          }}
                        >
                          <Input
                            size="small"
                            value={sw.width}
                            onChange={(ev, data) => {
                              dispatch(
                                updateSheet({
                                  sheetKey: selectedSheetKey,
                                  spacer: { name: sw.name, width: data.value },
                                })
                              );
                            }}
                          />
                        </div>
                      </div>
                    </>
                  ))}
                </div>
              </div>
            </div>

            {/* Shape Presets Section */}
            {selectedSheet?.shapePresets?.length > 0 && (
              <div
                ref={shapePresetsRef}
                style={{ background: "#F8F8F8", borderRadius: 16, padding: 8, paddingLeft: 16, marginTop: 32 }}
              >
                <div style={{ display: "flex", flexDirection: "row", gap: 4, marginBottom: 8 }}>
                  <ShapesMenu style={{ width: 20, height: 20 }} />
                  {t("stylesheet.nav.shapePresets")}
                </div>

                {/* Preset selector list — pills with icon + name */}
                <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                  {selectedSheet.shapePresets.map((preset) => (
                    <Button
                      key={preset.name}
                      appearance={selectedShapePresetName === preset.name ? "primary" : "subtle"}
                      icon={
                        <ShapePresetIcon
                          icon={getIconForShapeType(preset.shapeType, preset.radius)}
                          label={preset.label ?? ""}
                        />
                      }
                      onClick={() => setSelectedShapePresetName(preset.name)}
                    >
                      {preset.name}
                    </Button>
                  ))}
                </div>

                {/* Editor for selected preset */}
                {(() => {
                  const selectedPreset = selectedSheet.shapePresets.find((p) => p.name === selectedShapePresetName);
                  if (!selectedPreset) return null;

                  const previewColor =
                    selectedSheet.colors?.find((c) => c.name === selectedPreset.shapeColor)?.value ?? "#cccccc";
                  const previewOutlineColor =
                    selectedSheet.colors?.find((c) => c.name === selectedPreset.outlineColor)?.value ?? "#cccccc";

                  const textPreset = selectedSheet.presets?.find((p) => p.name === selectedPreset.textPreset);
                  const fontColorName = selectedPreset.inverted
                    ? (textPreset?.fontColor2 ?? textPreset?.fontColor)
                    : textPreset?.fontColor;
                  const previewTextColor = selectedSheet.colors?.find((c) => c.name === fontColorName)?.value;

                  const previewPaddingStyle = (pxField, percField, allPx, allPerc) => {
                    const px = pxField ?? allPx ?? 0;
                    const perc = percField ?? allPerc ?? 0;
                    return perc ? `calc(${px}px + ${perc}%)` : `${px}px`;
                  };

                  function dispatchPresetUpdate(fields) {
                    dispatch(
                      updateSheet({
                        sheetKey: selectedSheetKey,
                        shapePreset: { name: selectedShapePresetName, ...fields },
                      })
                    );
                  }

                  const spinVal = (v) => (isNaN(v) ? parseInt(v) : v);

                  return (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      {/* Row 1 left — Live preview */}
                      <div style={{ position: "relative", height: 100, border: "2px dotted #ccc" }}>
                        <CellShape
                          cellId="shape_preset_preview"
                          type={selectedPreset.shapeType}
                          radius={selectedPreset.radius}
                          color={previewColor}
                          outline={selectedPreset.outline}
                          outlineWeight={selectedPreset.outlineWeight}
                          outlineColor={previewOutlineColor}
                          shadow={selectedPreset.shadow}
                          margin={selectedPreset.margin ?? 0}
                          marginLeft={selectedPreset.marginLeft}
                          marginTop={selectedPreset.marginTop}
                          marginRight={selectedPreset.marginRight}
                          marginBottom={selectedPreset.marginBottom}
                          marginPerc={selectedPreset.marginPerc ?? 0}
                          marginLeftPerc={selectedPreset.marginLeftPerc}
                          marginTopPerc={selectedPreset.marginTopPerc}
                          marginRightPerc={selectedPreset.marginRightPerc}
                          marginBottomPerc={selectedPreset.marginBottomPerc}
                        >
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems:
                                selectedPreset.verticalAlign === "top"
                                  ? "flex-start"
                                  : selectedPreset.verticalAlign === "bottom"
                                    ? "flex-end"
                                    : "center",
                              justifyContent:
                                selectedPreset.textAlign === "left"
                                  ? "flex-start"
                                  : selectedPreset.textAlign === "right"
                                    ? "flex-end"
                                    : "center",
                              paddingLeft: previewPaddingStyle(
                                selectedPreset.paddingLeft,
                                selectedPreset.paddingLeftPerc,
                                selectedPreset.padding,
                                selectedPreset.paddingPerc
                              ),
                              paddingTop: previewPaddingStyle(
                                selectedPreset.paddingTop,
                                selectedPreset.paddingTopPerc,
                                selectedPreset.padding,
                                selectedPreset.paddingPerc
                              ),
                              paddingRight: previewPaddingStyle(
                                selectedPreset.paddingRight,
                                selectedPreset.paddingRightPerc,
                                selectedPreset.padding,
                                selectedPreset.paddingPerc
                              ),
                              paddingBottom: previewPaddingStyle(
                                selectedPreset.paddingBottom,
                                selectedPreset.paddingBottomPerc,
                                selectedPreset.padding,
                                selectedPreset.paddingPerc
                              ),
                              color: previewTextColor,
                              fontFamily: textPreset?.fontFamily ?? "Arial",
                              fontWeight: textPreset?.fontWeight ?? "normal",
                              fontStyle: textPreset?.fontStyle ?? "normal",
                              fontSize: textPreset?.fontSize ?? "12pt",
                              textDecoration: textPreset?.fontUnderlined ? "underline" : undefined,
                              overflow: "hidden",
                              pointerEvents: "none",
                            }}
                          >
                            {selectedShapePresetName}
                          </div>
                        </CellShape>
                      </div>

                      {/* Row 1 right — Shape type grid */}
                      <div>
                        <Label style={{ fontSize: 12, fontWeight: 600 }}>{t("menu.shape.select")}</Label>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            marginTop: 4,
                          }}
                        >
                          {availableShapes.map((shape) => (
                            <CommonTooltip key={shape.id} content={t(`shape.${shape.id}`)}>
                              <Button
                                appearance={
                                  selectedPreset.shapeType === shape.type &&
                                  (shape.radius !== undefined ? selectedPreset.radius === shape.radius : true)
                                    ? "primary"
                                    : "subtle"
                                }
                                icon={shape.icon}
                                style={{ minWidth: 0, padding: 4 }}
                                onClick={() =>
                                  dispatchPresetUpdate({
                                    shapeType: shape.type,
                                    radius: shape.radius ?? selectedPreset.radius,
                                  })
                                }
                              />
                            </CommonTooltip>
                          ))}
                        </div>
                      </div>

                      {/* Shape properties header */}
                      <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #e0e0e0", paddingTop: 8 }}>
                        <Label style={{ fontSize: 12, fontWeight: 700 }}>
                          {t("stylesheet.shapePresets.shapeProperties")}
                        </Label>
                      </div>

                      {/* Row 2 left — Fill + Outline + Shadow */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {/* Fill + Outline: labels row then dropdowns row */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "4px 8px",
                            alignItems: "start",
                          }}
                        >
                          {/* Labels row */}
                          <Label style={{ fontSize: 12, fontWeight: 600 }}>{t("menu.shape.fill")}</Label>
                          <Label style={{ fontSize: 12, fontWeight: 600 }}>{t("menu.shape.outline")}</Label>
                          {/* Dropdowns row */}
                          <Dropdown
                            style={{ minWidth: 0 }}
                            value={
                              <div
                                style={{
                                  width: 16,
                                  height: 16,
                                  backgroundColor: previewColor,
                                  border: "1px solid #ccc",
                                }}
                              />
                            }
                            selectedOptions={[selectedPreset.shapeColor]}
                            onOptionSelect={(_, d) => dispatchPresetUpdate({ shapeColor: d.optionValue })}
                          >
                            {selectedSheet.colors?.map((c) => (
                              <Option key={c.name} value={c.name}>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <div
                                    style={{
                                      width: 14,
                                      height: 14,
                                      backgroundColor: c.value,
                                      border: "1px solid #ccc",
                                      flexShrink: 0,
                                    }}
                                  />
                                  {c.name}
                                </div>
                              </Option>
                            ))}
                          </Dropdown>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <Dropdown
                              style={{ minWidth: 0 }}
                              value={
                                lineTypes.find(
                                  (l) =>
                                    l.outline === selectedPreset.outline && l.weight === selectedPreset.outlineWeight
                                )?.icon ?? t("menu.shape.outline.none")
                              }
                              selectedOptions={[
                                lineTypes.find(
                                  (l) =>
                                    l.outline === selectedPreset.outline && l.weight === selectedPreset.outlineWeight
                                )?.id ?? "none",
                              ]}
                              onOptionSelect={(_, d) => {
                                const lt = lineTypes.find((l) => l.id === d.optionValue);
                                if (lt) dispatchPresetUpdate({ outline: lt.outline, outlineWeight: lt.weight ?? 1 });
                              }}
                            >
                              {lineTypes.map((lt) => (
                                <Option key={lt.id} value={lt.id}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    {lt.icon && <div style={{ width: 16, height: 16 }}>{lt.icon}</div>}
                                    {t("menu.shape.outline." + lt.id)}
                                  </div>
                                </Option>
                              ))}
                            </Dropdown>
                            <Dropdown
                              style={{ minWidth: 0 }}
                              disabled={selectedPreset.outline === "none"}
                              value={
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor:
                                      selectedSheet.colors?.find((c) => c.name === selectedPreset.outlineColor)
                                        ?.value ?? "#cccccc",
                                    border: "1px solid #ccc",
                                  }}
                                />
                              }
                              selectedOptions={[selectedPreset.outlineColor]}
                              onOptionSelect={(_, d) => dispatchPresetUpdate({ outlineColor: d.optionValue })}
                            >
                              {selectedSheet.colors?.map((c) => (
                                <Option key={c.name} value={c.name}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <div
                                      style={{
                                        width: 14,
                                        height: 14,
                                        backgroundColor: c.value,
                                        border: "1px solid #ccc",
                                        flexShrink: 0,
                                      }}
                                    />
                                    {c.name}
                                  </div>
                                </Option>
                              ))}
                            </Dropdown>
                          </div>
                        </div>

                        {/* Shadow */}
                        <div>
                          <Label style={{ fontSize: 12, fontWeight: 600 }}>{t("menu.shape.shadow")}</Label>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                            <SpinButton
                              size="small"
                              style={{ minWidth: 0, width: 60 }}
                              min={0}
                              max={100}
                              step={1}
                              value={Math.round((selectedPreset.shadow ?? 0) * 100)}
                              onChange={(_, d) =>
                                dispatchPresetUpdate({
                                  shadow: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value) / 100,
                                })
                              }
                            />
                            <Text>%</Text>
                          </div>
                        </div>
                      </div>

                      {/* Row 2 right — Size relative to cell */}
                      <div>
                        <Label style={{ fontSize: 12, fontWeight: 600 }}>{t("menu.shape.size.title")}</Label>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "auto 48px auto 48px auto",
                            rowGap: 4,
                            columnGap: 4,
                            marginTop: 4,
                            alignItems: "center",
                          }}
                        >
                          {/* header */}
                          <div />
                          <Label style={{ fontSize: 10, textAlign: "center" }}>px</Label>
                          <div />
                          <Label style={{ fontSize: 10, textAlign: "center" }}>%</Label>
                          <div />
                          {/* All */}
                          <Label style={{ fontSize: 11 }}>{t("menu.shape.size.all")}</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.margin ?? 0}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                margin: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.marginPerc ?? 0}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                marginPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ margin: null, marginPerc: null })}
                          />
                          {/* Left */}
                          <Label style={{ fontSize: 11 }}>Left</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.marginLeft ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                marginLeft: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.marginLeftPerc ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                marginLeftPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ marginLeft: null, marginLeftPerc: null })}
                          />
                          {/* Top */}
                          <Label style={{ fontSize: 11 }}>Top</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.marginTop ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                marginTop: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.marginTopPerc ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                marginTopPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ marginTop: null, marginTopPerc: null })}
                          />
                          {/* Right */}
                          <Label style={{ fontSize: 11 }}>Right</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.marginRight ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                marginRight: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.marginRightPerc ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                marginRightPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ marginRight: null, marginRightPerc: null })}
                          />
                          {/* Bottom */}
                          <Label style={{ fontSize: 11 }}>Bottom</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.marginBottom ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                marginBottom: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.marginBottomPerc ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                marginBottomPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ marginBottom: null, marginBottomPerc: null })}
                          />
                        </div>
                      </div>

                      {/* Text properties header */}
                      <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #e0e0e0", paddingTop: 8 }}>
                        <Label style={{ fontSize: 12, fontWeight: 700 }}>
                          {t("stylesheet.shapePresets.textProperties")}
                        </Label>
                      </div>

                      {/* Row 3 left — Text style + Invert + Align */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {/* Text Style */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <Label style={{ fontSize: 12, fontWeight: 600 }}>{t("menu.style.font")}</Label>
                          <Dropdown
                            style={{ minWidth: 0 }}
                            value={selectedPreset.textPreset ?? ""}
                            selectedOptions={[selectedPreset.textPreset ?? ""]}
                            onOptionSelect={(_, d) => dispatchPresetUpdate({ textPreset: d.optionValue })}
                          >
                            {selectedSheet.presets?.map((p) => (
                              <Option key={p.name} value={p.name}>
                                {p.name}
                              </Option>
                            ))}
                          </Dropdown>
                        </div>

                        {/* Invert */}
                        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Switch
                            checked={selectedPreset.inverted ?? false}
                            onChange={(_, data) => dispatchPresetUpdate({ inverted: data.checked })}
                          />
                          <Label style={{ fontSize: 12, fontWeight: 600 }}>{t("menu.style.invert")}</Label>
                        </div>

                        {/* Align */}
                        <div>
                          <Label style={{ fontSize: 12, fontWeight: 600 }}>{t("menu.style.align")}</Label>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "36px 36px 36px",
                              gridTemplateRows: "auto auto auto",
                              marginTop: 4,
                            }}
                          >
                            {[
                              { icon: <AlignTopLeft />, textAlign: "left", verticalAlign: "top" },
                              { icon: <AlignTopCenter />, textAlign: "center", verticalAlign: "top" },
                              { icon: <AlignTopRight />, textAlign: "right", verticalAlign: "top" },
                              { icon: <AlignCenterLeft />, textAlign: "left", verticalAlign: "middle" },
                              { icon: <AlignCenterCenter />, textAlign: "center", verticalAlign: "middle" },
                              { icon: <AlignCenterRight />, textAlign: "right", verticalAlign: "middle" },
                              { icon: <AlignBottomLeft />, textAlign: "left", verticalAlign: "bottom" },
                              { icon: <AlignBottomCenter />, textAlign: "center", verticalAlign: "bottom" },
                              { icon: <AlignBottomRight />, textAlign: "right", verticalAlign: "bottom" },
                            ].map(({ icon, textAlign, verticalAlign }) => (
                              <Button
                                key={`${textAlign}-${verticalAlign}`}
                                appearance={
                                  selectedPreset.textAlign === textAlign &&
                                  selectedPreset.verticalAlign === verticalAlign
                                    ? "primary"
                                    : "subtle"
                                }
                                icon={React.cloneElement(icon, { style: { width: 24, height: 24 } })}
                                style={{ minWidth: 0, padding: 4 }}
                                onClick={() => dispatchPresetUpdate({ textAlign, verticalAlign })}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Row 3 right — Text margins (padding) */}
                      <div>
                        <Label style={{ fontSize: 12, fontWeight: 600 }}>{t("menu.style.padding")}</Label>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "auto 48px auto 48px auto",
                            rowGap: 4,
                            columnGap: 4,
                            marginTop: 4,
                            alignItems: "center",
                          }}
                        >
                          {/* header */}
                          <div />
                          <Label style={{ fontSize: 10, textAlign: "center" }}>px</Label>
                          <div />
                          <Label style={{ fontSize: 10, textAlign: "center" }}>%</Label>
                          <div />
                          {/* All */}
                          <Label style={{ fontSize: 11 }}>{t("menu.shape.size.all")}</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.padding ?? 0}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                padding: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.paddingPerc ?? 0}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                paddingPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ padding: null, paddingPerc: null })}
                          />
                          {/* Left */}
                          <Label style={{ fontSize: 11 }}>Left</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.paddingLeft ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                paddingLeft: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.paddingLeftPerc ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                paddingLeftPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ paddingLeft: null, paddingLeftPerc: null })}
                          />
                          {/* Top */}
                          <Label style={{ fontSize: 11 }}>Top</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.paddingTop ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                paddingTop: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.paddingTopPerc ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                paddingTopPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ paddingTop: null, paddingTopPerc: null })}
                          />
                          {/* Right */}
                          <Label style={{ fontSize: 11 }}>Right</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.paddingRight ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                paddingRight: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.paddingRightPerc ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                paddingRightPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ paddingRight: null, paddingRightPerc: null })}
                          />
                          {/* Bottom */}
                          <Label style={{ fontSize: 11 }}>Bottom</Label>
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.paddingBottom ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                paddingBottom: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Add16Filled style={{ justifySelf: "center" }} />
                          <SpinButton
                            size="small"
                            style={{ minWidth: 0, width: 48 }}
                            value={selectedPreset.paddingBottomPerc ?? null}
                            onChange={(_, d) =>
                              dispatchPresetUpdate({
                                paddingBottomPerc: spinVal(isNaN(d.value) ? parseInt(d.displayValue) : d.value),
                              })
                            }
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<XIcon />}
                            style={{ minWidth: 0, padding: 2 }}
                            onClick={() => dispatchPresetUpdate({ paddingBottom: null, paddingBottomPerc: null })}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={isPanelVariant ? classes.PanelFooter : undefined}
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "2px",
          margin: 8,
          marginRight: 48,
          justifyContent: "right",
          flex: "0 0 auto",
        }}
      >
        {!props.isInDialog ? (
          <>
            <Button
              appearance="subtle"
              onClick={() => {
                // §9.5 — in the host panel there is no Office dialog to reply to;
                // route Cancel through the host bridge instead of messageParent.
                if (isHostPanel) {
                  dispatch(abandonChangesOnAll());
                  window.parent.postMessage({ type: "gridd-stylesheet-cancel" }, "*");
                } else {
                  Office.context.ui.messageParent(JSON.stringify({ isCancelled: true }));
                }
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              appearance="primary"
              className={classes.Apply}
              onClick={() => {
                // §9.4/§9.5 — host owns persistence: commit drafts and post the
                // saved sheet to the parent frame instead of Office.messageParent.
                if (isHostPanel) {
                  dispatch(applyChangesOnAll());
                  if (selectedSheetKey !== null) {
                    dispatch(setSelectedSheet({ sheetName: selectedSheetKey }));
                  }
                  dispatch(storeSheets({ to: "host" }));
                  return;
                }
                // send back the content of styleSheet.changedSheets
                Office.context.ui.messageParent(JSON.stringify({ isCancelled: false, styleSheet }));
              }}
            >
              {t("apply")}
            </Button>
          </>
        ) : (
          <>
            <Button
              id="cancel_button"
              size="small"
              className={classes.Cancel}
              onClick={() => {
                dispatch(abandonChangesOnAll());
                dispatch(closeStyleSheetDialog());
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              id="apply_button"
              size="small"
              appearance="primary"
              className={classes.Apply}
              disabled={colorPickerOpen}
              style={{
                backgroundColor: colorPickerOpen ? "#e8e8e8" : "#0065FD",
                cursor: colorPickerOpen ? "not-allowed" : "pointer",
              }}
              onClick={() => {
                if (colorPickerOpen) return;
                dispatch(applyChangesOnAll());
                if (selectedSheetKey !== null) {
                  dispatch(setSelectedSheet({ sheetName: selectedSheetKey }));
                }
                dispatch(storeSheets({ to: "localStorage" }));
                dispatch(storeSheets({ to: "presentation" }));
                dispatch(closeStyleSheetDialog());
              }}
            >
              {t("apply")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

StyleSheetEditor.propTypes = {
  isInDialog: PropTypes.bool.isRequired,
  onSelectedKeyChange: PropTypes.func,
};

export default StyleSheetEditor;
